import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============ LEADS API ============

export const leadsApi = {
  list: (params = {}) => api.get('/leads', { params }),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  uploadCSV: (file, defaultProductId) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = defaultProductId ? { default_product_id: defaultProductId } : undefined;
    return api.post('/leads/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params
    });
  },
  getStats: () => api.get('/leads/stats'),
  getCommunications: (id) => api.get(`/leads/${id}/communications`),
  updateStage: (id, stage) => api.post(`/leads/${id}/stage/${stage}`),
  optOut: (id) => api.post(`/leads/${id}/opt-out`),
  pauseAutomation: (id) => api.post(`/leads/${id}/pause-automation`),
  resumeAutomation: (id) => api.post(`/leads/${id}/resume-automation`)
};

// ============ AI AGENTS API ============

export const aiApi = {
  triggerScheduling: (leadId) => api.post(`/ai/schedule/${leadId}`),
  sendFollowUp: (leadId, type) => api.post(`/ai/follow-up/${leadId}`, null, {
    params: { follow_up_type: type }
  }),
  analyzeDiscovery: (leadId, data) => api.post(`/ai/analyze-discovery/${leadId}`, data),
  getDiscoveryInsights: (leadId) => api.get(`/ai/discovery-insights/${leadId}`),
  getActivityLogs: (params = {}) => api.get('/ai/activity-logs', { params }),
  getWalletUsage: () => api.get('/ai/wallet-usage')
};

// ============ PROPOSALS API ============

export const proposalsApi = {
  list: (params = {}) => api.get('/proposals', { params }),
  get: (id) => api.get(`/proposals/${id}`),
  generate: (leadId) => api.post(`/proposals/generate/${leadId}`),
  update: (id, data) => api.put(`/proposals/${id}`, data),
  submitForApproval: (id) => api.post(`/proposals/${id}/submit-for-approval`),
  approve: (id) => api.post(`/proposals/${id}/approve`),
  reject: (id, reason) => api.post(`/proposals/${id}/reject`, null, {
    params: { reason }
  }),
  send: (id) => api.post(`/proposals/${id}/send`),
  delete: (id) => api.delete(`/proposals/${id}`)
};

// ============ DEALS API ============

export const dealsApi = {
  markWon: (leadId, dealValue, notes) => api.post(`/deals/${leadId}/mark-won`, null, {
    params: { deal_value: dealValue, notes }
  }),
  markLost: (leadId, reason, notes) => api.post(`/deals/${leadId}/mark-lost`, null, {
    params: { lost_reason: reason, notes }
  }),
  getPipelineSummary: () => api.get('/deals/pipeline-summary'),
  getFollowUpDue: () => api.get('/deals/follow-up-due'),
  scheduleFollowUp: (leadId, days, note) => api.post(`/deals/${leadId}/schedule-follow-up`, null, {
    params: { days, note }
  })
};

// ============ WALLET API ============

export const walletApi = {
  getBalance: () => api.get('/wallet/balance'),
  topUp: (amount, method = 'manual') => api.post('/wallet/top-up', {
    amount,
    payment_method: method
  }),
  getTransactions: (params = {}) => api.get('/wallet/transactions', { params }),
  getUsageSummary: () => api.get('/wallet/usage-summary'),
  getPricing: () => api.get('/wallet/pricing'),
  adminCredit: (userId, amount, reason) => api.post(`/wallet/admin/credit/${userId}`, null, {
    params: { amount, reason }
  }),
  getAllBalances: () => api.get('/wallet/admin/all-balances')
};

// ============ SETTINGS API ============

export const settingsApi = {
  getAutomation: () => api.get('/settings/automation'),
  updateAutomation: (data) => api.put('/settings/automation', data),
  getEmailTemplates: () => api.get('/settings/email-templates'),
  updateEmailTemplates: (templates) => api.put('/settings/email-templates', templates),
  getKillSwitches: () => api.get('/settings/kill-switches'),
  updateKillSwitches: (switches) => api.put('/settings/kill-switches', switches),
  getDashboardMetrics: () => api.get('/settings/dashboard-metrics'),
  getIntegrations: () => api.get('/settings/integrations'),
  updateEmailIntegration: (config) => api.put('/settings/integrations/email', config),
  updateCalendlyIntegration: (config) => api.put('/settings/integrations/calendly', config),
  getSchedulerStatus: () => api.get('/settings/scheduler/status')
};

// ============ PENDING ACTIONS API ============

export const pendingActionsApi = {
  list: (params = {}) => api.get('/pending-actions', { params }),
  getQueue: (params = {}) => api.get('/pending-actions/queue', { params }),
  get: (id) => api.get(`/pending-actions/${id}`),
  generate: (id) => api.post(`/pending-actions/${id}/generate`),
  approve: (id) => api.post(`/pending-actions/${id}/approve`),
  send: (id) => api.post(`/pending-actions/${id}/send`),
  bulkSend: (actionIds) => api.post('/pending-actions/bulk-send', { action_ids: actionIds }),
  delete: (id) => api.delete(`/pending-actions/${id}`)
};

// ============ USAGE LOG API ============

export const usageLogApi = {
  list: (params = {}) => api.get('/usage-log', { params }),
  getSummary: (days = 30) => api.get('/usage-log/summary', { params: { days } }),
  getLeadUsage: (leadId) => api.get(`/usage-log/lead/${leadId}`),
  getDebugMode: () => api.get('/usage-log/debug-mode'),
  setDebugMode: (enabled) => api.put('/usage-log/debug-mode', null, { params: { enabled } })
};

// ============ SMS API ============

export const smsApi = {
  getStatus: () => api.get('/sms/status'),
  sendToLead: (leadId, message, fromNumber = null) => 
    api.post(`/sms/send/${leadId}`, { lead_id: leadId, message, from_number: fromNumber }),
  sendAiSms: (leadId, smsType = 'follow_up') => 
    api.post(`/sms/send-ai/${leadId}`, null, { params: { sms_type: smsType } }),
  getMessageStatus: (messageSid) => api.get(`/sms/message/${messageSid}`),
  getRecentMessages: (limit = 20) => api.get('/sms/recent', { params: { limit } }),
  callLead: (leadId, twiml = null, url = null) => 
    api.post(`/sms/call/${leadId}`, { lead_id: leadId, twiml, url }),
  getCallStatus: (callSid) => api.get(`/sms/call/${callSid}/status`),
  getRecentCalls: (limit = 20) => api.get('/sms/calls/recent', { params: { limit } })
};

// ============ AUTH API ============

export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  listUsers: () => api.get('/auth/users'),
  updateRole: (userId, role) => api.put(`/auth/users/${userId}/role`, null, {
    params: { role }
  }),
  deactivateUser: (userId) => api.put(`/auth/users/${userId}/deactivate`)
};


// ============ PRODUCTS & SERVICES API ============

export const productsApi = {
  list: (params = {}) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getStats: (id) => api.get(`/products/${id}/stats`),
  performanceDashboard: () => api.get('/products/_meta/dashboard-performance'),
  // Agents
  listAgents: (id) => api.get(`/products/${id}/agents`),
  updateAgent: (id, agentType, data) =>
    api.put(`/products/${id}/agents/${agentType}`, data),
  agentsRuntime: (id) => api.get(`/products/${id}/agents-runtime`),
  // AI Guidance Rules (also exposed under /fit-rules for backwards-compat)
  listGuidanceRules: (id) => api.get(`/products/${id}/ai-guidance-rules`),
  addGuidanceRule: (id, data) => api.post(`/products/${id}/ai-guidance-rules`, data),
  updateGuidanceRule: (id, ruleId, data) => api.put(`/products/${id}/ai-guidance-rules/${ruleId}`, data),
  deleteGuidanceRule: (id, ruleId) => api.delete(`/products/${id}/ai-guidance-rules/${ruleId}`),
  // Workflow
  workflow: (id) => api.get(`/products/${id}/workflow`)
};

// ============ INTEGRATIONS API ============

export const integrationsApi = {
  postLead: (data) => api.post('/integrations/leads', data),
  postEvent: (data) => api.post('/integrations/events', data),
  listEvents: (params = {}) => api.get('/integrations/events', { params }),
  testLead: (data) => api.post('/integrations/test/lead', data),
  testEvent: (data) => api.post('/integrations/test/event', data),
  eventTemplates: () => api.get('/integrations/test/event-templates')
};

// ============ LEADS API (timeline) ============

export const leadsTimelineApi = {
  timeline: (leadId, params = {}) => api.get(`/leads/${leadId}/timeline`, { params })
};

// ============ SIMULATIONS API (Sandbox lifecycle runner) ============

export const simulationsApi = {
  actionTypes: () => api.get('/simulations/action-types'),
  run: (data) => api.post('/simulations/run', data),
  list: (params = {}) => api.get('/simulations', { params }),
  get: (actionId) => api.get(`/simulations/${actionId}`),
  replay: (actionId) => api.post(`/simulations/replay/${actionId}`)
};

export default api;

